/* eslint-disable @typescript-eslint/naming-convention */
import { DateAxis, ValueAxis, XYChart } from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';

import { timeScalesEnum } from '../../../../Monitor/Shared/enums/timeScales.enum';

import { SeriesModel } from '../series.model';
import { ChartMetadataModel } from '../chart-metadata.model';
import { DateAndValueXYChartOptionsModel } from '../date-and-value-x-y-chart-options.model';
import { StackedRiverChartModel } from '../StackedRiverChart/stacked-river-chart.model';

export class PositiveNegativeRiverChartModel extends StackedRiverChartModel {
    readonly singleSeries: boolean = false;
    public declare amchart: XYChart;
    dateAxis: DateAxis;
    valueAxis: ValueAxis;
    scale: timeScalesEnum;
    tooltipHovered: boolean = true;
    tooltipDisplayInterval: NodeJS.Timeout;
    tooltipHoverIndex: number;
    constructor(
        series: SeriesModel[],
        options: DateAndValueXYChartOptionsModel = {},
        metadata?: ChartMetadataModel,
    ) {
        super(series, options, metadata);
    }

    public initChart(element: HTMLElement): void {
        super.initChart(element);

        if ((this.options as DateAndValueXYChartOptionsModel).minYValue) {
            this.valueAxis.min = (this.options as DateAndValueXYChartOptionsModel).minYValue;
        }
        if ((this.options as DateAndValueXYChartOptionsModel).maxYValue) {
            this.valueAxis.max = (this.options as DateAndValueXYChartOptionsModel).maxYValue;
        }

        this.valueAxis.renderer.grid.template.above = true;
        this.valueAxis.numberFormatter.numberFormat = '#a %';

        if ((this.options as DateAndValueXYChartOptionsModel).horizontalLines?.length > 0) {
            (this.options as DateAndValueXYChartOptionsModel).horizontalLines.forEach((lineDef) => {
                this.addAxisLine(this.valueAxis, lineDef, false);
            });
            this.amchart.legend.addData((this.options as DateAndValueXYChartOptionsModel).horizontalLines);

            setTimeout(() => {
                this.amchart.legend.markers.values.forEach(marker => {
                    if (marker.dataItem.index > 1) {
                        marker.height = 2;
                    }
                });

                this.amchart.legend.itemContainers.values.forEach(item => {
                    if (item.dataItem.index > 1) {
                        item.width = 180;
                    }
                });
            }, 100);
        }
    }

    public updateData(data: any[]) {
        this.rawData = data;
        if (!data || data.some(series => series === null)) {
            this.amchart.data = null;
            return;
        }

        const seriesIds = [];
        const seriesLabels = [];
        (this.options as DateAndValueXYChartOptionsModel).seriesDefinitions.forEach(series => {
            seriesIds.push(series.key);
            seriesLabels.push(series.label);
        });

        // It relies on all data points on the same date to calculate the total, and each boundary point
        const flatData = [];
        for (let i = 0; i < data[0].length; i++) {
            const dataPoint = {};
            dataPoint['time'] = data[0][i].xValue;
            dataPoint['label'] = moment(data[0][i].xValue).format('MMM DD, YYYY hh:mm A z');
            const sum = {};
            for (let j = 0; j < seriesIds.length; j++) {
                sum[seriesIds[j]] = 0;
                if (!data[0][i]) {
                    // If there's no data for that series/node, just skip it
                    continue;
                }
                const key = seriesIds[j];
                const value = data[0][i][`yPercent_${key}`];
                dataPoint[key] = value;
                dataPoint[key + '_lo'] = 0;
                dataPoint[key + '_hi'] = value;
                dataPoint[seriesIds[j] + 'Percent'] = data[0][i][`yPercent_${key}`].toFixed(2);
            }
            flatData.push(dataPoint);
        }

        this.amchart.data = (JSON.parse(JSON.stringify(flatData)));
        this.updateSeriesStyles();

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.valueAxis.disabled = true;
            this.dateAxis.disabled = true;
            if (this.amseries) {
                this.amseries.disabled = true;
            }
            this.showNoContentMessage();
        }

        this.ready.next(true);
    }
}
